import React, { useEffect, useState } from 'react';
import { JobFairStatus } from 'app/types';
import moment from 'moment';
import { useAppContext } from 'app/context/app-context';
import { Alert } from 'react-bootstrap-v5';
import { getTimezoneOffset } from 'app/helpers/MiscUtils';

const mapper = new Map<JobFairStatus, string>([
  [JobFairStatus.Upcoming, 'success'],
  [JobFairStatus.Started, 'warning'],
  [JobFairStatus.Ended, 'danger'],
]);

export const JobFairStatusComponent: React.FC = () => {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const { jobFair } = useAppContext();

  useEffect(() => {
    if (!jobFair) return;

    const timer = setInterval(() => {
      // 1. Parse event time with timezone offset and convert to UTC
      const timeZone = getTimezoneOffset(jobFair.timezone);
      const startTime = moment(
        `${jobFair.startDate} ${jobFair.startTime} ${timeZone}`,
        'YYYY-MM-DD HH:mm ZZ' // Use 24-hour format if no AM/PM is provided
      ).utc();

      const endTime = startTime.clone()
        .add(jobFair.durationHours, 'hours')
        .add(jobFair.durationMinutes, 'minutes');

      // 2. Get current time in UTC
      const now = moment().utc();

      // 3. Dynamically determine status instead of relying on prop
      let status = JobFairStatus.Ended;
      if (now.isBefore(startTime)) {
        status = JobFairStatus.Upcoming;
      } else if (now.isBetween(startTime, endTime)) {
        status = JobFairStatus.Started;
      } else if (now.isBetween(endTime, endTime.clone().add(1.5, 'hours'))) {
        status = JobFairStatus.ClosingSession;
      }

      // 4. Calculate time remaining based on dynamic status
      if (status === JobFairStatus.Upcoming) {
        const duration = moment.duration(startTime.diff(now));
        if (duration.asMilliseconds() > 0) {
          setTimeRemaining(
            `Starts in ${duration.days()}d ${duration.hours()}h ${duration.minutes()}m`
          );
        } else {
          setTimeRemaining("Starting soon...");
        }
      } else if (status === JobFairStatus.Started) {
        const duration = moment.duration(endTime.diff(now));
        setTimeRemaining(
          `Event ends in ${duration.hours()}h ${duration.minutes()}m`
        );
      } else {
        const formattedDate = startTime.format('MMM DD, YYYY h:mm A [UTC]');
        setTimeRemaining(
          `Ended on ${formattedDate} | Duration: ${jobFair.durationHours}h ${jobFair.durationMinutes}m`
        );
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [jobFair]);

  if (!jobFair) return null;

  return (
    <Alert variant={mapper.get(jobFair.status)}>
      {timeRemaining}
    </Alert>
  );
};